import { datadogLogs } from '@datadog/browser-logs';

import { VERSION } from './constants';

// Datadog Browser Logs only - RUM/Session Replay must never be enabled for this app.
const SERVICE = 'pulse-cms';

// Remove alpha version from the reported release.
// E.g. 1.8.4-alpha.1 will be stored as 1.8.4-alpha
const release = VERSION ? VERSION.replace(/-alpha(\.\d)/, '-alpha') : undefined;

const ignoredErrors = [
  // API errors
  /^Request failed with status code.*/,
  'Network Error',
  'Request aborted',
  // Browser errors
  'ResizeObserver loop limit exceeded',
  // Not supported browser errors
  "'window.requestAnimationFrame' is not a function",
  "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
  // Extension errors
  /^safari-extension.*/,
  // Nothing useful errors
  'Non-Error exception captured',
  'Non-Error promise rejection captured',
  'Error: SyntaxError: SyntaxError',
  'Env not inited yet',
  'this.ws.close is not a function',
  "Unable to get property 'entry' of undefined or null reference",
  "TypeError: undefined is not an object (evaluating 'p.manager')",
  'An event processor returned null, will not send event.'
];

const deniedUrls = [
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i
];

const matches = (value, patterns) =>
  patterns.some(pattern => (pattern instanceof RegExp ? pattern.test(value) : value === pattern));

const resolvedEnv = process.env.REACT_APP_DATADOG_ENV || process.env.NODE_ENV;

const isReady = () => Boolean(process.env.REACT_APP_DATADOG_CLIENT_TOKEN);

if (isReady()) {
  datadogLogs.init({
    clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    site: process.env.REACT_APP_DATADOG_SITE || 'datadoghq.com',
    service: process.env.REACT_APP_DATADOG_SERVICE || SERVICE,
    env: resolvedEnv,
    version: release,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    pulse_env: `cai-${resolvedEnv}`,
    install: window.location.hostname,
    beforeSend: log => {
      if (
        matches(log.message || '', ignoredErrors) ||
        matches(log.error?.stack || '', deniedUrls)
      ) {
        return false;
      }
    }
  });

  // Matches the `pulse_env:cai-<env>` tag convention from the backend's Datadog log forwarding
  // (see pulse-infra-core's DATADOG_TAGS), so frontend and backend logs can be correlated.
  datadogLogs.setGlobalContextProperty('pulse_env', `cai-${resolvedEnv}`);

  // The CMS is deployed once per customer at their own domain (unlike the widgets, there's no
  // static `client` identifier available before login) - `install.name` lets you tell installs
  // apart without relying on parsing the auto-collected `view.url` on every log.
  datadogLogs.setGlobalContextProperty('install', { hostname: window.location.hostname });
}

const logError = (error, info) => {
  if (!isReady()) {
    return;
  }

  // Pass the Error via the dedicated 3rd arg so Datadog extracts kind/message/stack itself -
  // nesting it in the context object instead serializes native Errors as `{}` (no own properties).
  datadogLogs.logger.error(
    error?.message || 'Unhandled error',
    { componentStack: info?.componentStack },
    error instanceof Error ? error : undefined
  );
};

const setUser = user => {
  if (isReady()) {
    datadogLogs.setUser(user);
  }
};

const setContext = (key, value) => {
  if (isReady()) {
    datadogLogs.setGlobalContextProperty(key, value);
  }
};

const setTag = (key, value) => {
  if (isReady()) {
    datadogLogs.setGlobalContextProperty(key, value);
  }
};

const clearScope = () => {
  if (isReady()) {
    datadogLogs.clearUser();
  }
};

export { logError, setUser, setContext, setTag, clearScope };
